import { useState, useEffect } from 'react';
import { DollarSign, Save, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { api } from '../../api/client';

// Helper to capitalize names
const formatName = (name) => {
    if (!name) return '-';
    return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export default function AdminPricing() {
    const [pricing, setPricing] = useState([]);
    const [availableEndpoints, setAvailableEndpoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({});
    
    // For creating new pricing
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newPricing, setNewPricing] = useState({
        endpoint: '',
        price_per_call: '',
        description: ''
    });

    useEffect(() => {
        fetchPricing();
        fetchAvailableEndpoints();
    }, []);

    // Auto-dismiss success messages after 3 seconds
    useEffect(() => {
        if (message.type === 'success') {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const fetchPricing = async () => {
        try {
            const res = await api.get('/api/admin/pricing');
            setPricing(res.data);
        } catch (err) {
            console.error('Failed to load pricing:', err);
            setMessage({ type: 'error', text: 'Failed to load pricing data' });
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableEndpoints = async () => {
        try {
            const res = await api.get('/api/admin/pricing/available-endpoints');
            setAvailableEndpoints(res.data);
        } catch (err) {
            console.error('Failed to load available endpoints:', err);
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setEditValues({
            price_per_call: item.price_per_call.toString(),
            is_active: item.is_active,
            description: item.description || ''
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditValues({});
    };

    const handleSave = async (id) => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('price_per_call', editValues.price_per_call);
            formData.append('is_active', editValues.is_active);
            if (editValues.description) {
                formData.append('description', editValues.description);
            }

            await api.put(`/api/admin/pricing/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setMessage({ type: 'success', text: 'Pricing updated successfully!' });
            setEditingId(null);
            setEditValues({});
            fetchPricing();
        } catch (err) {
            console.error('Failed to update pricing:', err);
            setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update pricing' });
        } finally {
            setSaving(false);
        }
    };

    const handleCreatePricing = async (e) => {
        e.preventDefault();
        if (!newPricing.endpoint || !newPricing.price_per_call) {
            setMessage({ type: 'error', text: 'Please select an endpoint and enter a price' });
            return;
        }

        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('endpoint', newPricing.endpoint);
            formData.append('price_per_call', newPricing.price_per_call);
            if (newPricing.description) {
                formData.append('description', newPricing.description);
            }

            await api.post('/api/admin/pricing', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setMessage({ type: 'success', text: 'Pricing created successfully!' });
            setShowCreateForm(false);
            setNewPricing({ endpoint: '', price_per_call: '', description: '' });
            fetchPricing();
            fetchAvailableEndpoints();
        } catch (err) {
            console.error('Failed to create pricing:', err);
            setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create pricing' });
        } finally {
            setSaving(false);
        }
    };

    const unconfiguredEndpoints = availableEndpoints.filter(e => !e.has_pricing);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    
                    API Pricing Management
                </h1>

            </div>

            {/* Message Alert */}
            {message.text && (
                <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
                    message.type === 'success' 
                        ? 'bg-green-50 text-green-800 border border-green-200' 
                        : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                    {message.type === 'success' ? (
                        <CheckCircle className="w-5 h-5" />
                    ) : (
                        <AlertCircle className="w-5 h-5" />
                    )}
                    {message.text}
                </div>
            )}

            {/* Warning if no pricing configured */}
            {pricing.length === 0 && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-800">
                        <AlertCircle className="w-5 h-5" />
                        <span className="font-medium">No pricing configured!</span>
                    </div>
                    <p className="text-yellow-700 text-sm mt-1">
                        Without pricing configuration, all paid endpoints will be billed at $0.00.
                        Please configure pricing for paid endpoints below.
                    </p>
                </div>
            )}

            {/* Create New Pricing */}
            {unconfiguredEndpoints.length > 0 && (
                <div className="mb-6">
                    {!showCreateForm ? (
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            + Add New Endpoint Pricing
                        </button>
                    ) : (
                        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                            <h3 className="text-lg font-semibold mb-4">Add New Endpoint Pricing</h3>
                            <form onSubmit={handleCreatePricing} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Select Endpoint
                                    </label>
                                    <select
                                        value={newPricing.endpoint}
                                        onChange={(e) => setNewPricing({ ...newPricing, endpoint: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                        required
                                    >
                                        <option value="">-- Select an endpoint --</option>
                                        {unconfiguredEndpoints.map((ep) => (
                                            <option key={ep.endpoint} value={ep.endpoint}>
                                                {ep.endpoint_name} ({ep.endpoint})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Price per Call (USD)
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={newPricing.price_per_call}
                                            onChange={(e) => setNewPricing({ ...newPricing, price_per_call: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                            placeholder="2.00"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description (Optional)
                                    </label>
                                    <textarea
                                        value={newPricing.description}
                                        onChange={(e) => setNewPricing({ ...newPricing, description: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                        rows={2}
                                        placeholder="Optional description for this pricing"
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {saving ? (
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Save className="w-4 h-4" />
                                        )}
                                        Create Pricing
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            setNewPricing({ endpoint: '', price_per_call: '', description: '' });
                                        }}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            )}

            {/* Pricing Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Endpoint
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Category
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Price per Call
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {pricing.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                    No pricing configured. Add pricing for paid endpoints using the button above.
                                </td>
                            </tr>
                        ) : (
                            pricing.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900">{item.endpoint_name}</div>
                                        <div className="text-xs text-gray-500 font-mono">{item.endpoint}</div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {formatName(item.category)}
                                    </td>
                                    <td className="px-6 py-4">
                                        {editingId === item.id ? (
                                            <div className="relative w-32 flex items-center">
                                                <span className="absolute left-3 text-gray-500 pointer-events-none">$</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={editValues.price_per_call}
                                                    onChange={(e) => setEditValues({ ...editValues, price_per_call: e.target.value })}
                                                    className="w-full border border-gray-300 rounded pl-7 pr-2 py-1.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                />
                                            </div>
                                        ) : (
                                            <span className="text-sm font-medium text-gray-900">
                                                ${item.price_per_call.toFixed(2)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        {editingId === item.id ? (
                                            <select
                                                value={editValues.is_active}
                                                onChange={(e) => setEditValues({ ...editValues, is_active: e.target.value === 'true' })}
                                                className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                            >
                                                <option value="true">Active</option>
                                                <option value="false">Inactive</option>
                                            </select>
                                        ) : (
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                item.is_active 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {item.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        {editingId === item.id ? (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleSave(item.id)}
                                                    disabled={saving}
                                                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                                >
                                                    {saving ? (
                                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                        <Save className="w-3 h-3" />
                                                    )}
                                                    Save
                                                </button>
                                                <button
                                                    onClick={handleCancelEdit}
                                                    className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                                            >
                                                Edit
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            
        </div>
    );
}
